import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  PaymentMethodCreateInput,
  PaymentMethodDTO,
  PaymentMethodMethod,
  PaymentMethodUpdateInput,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';

/**
 * Owner/staff management of the accepted-methods catalog (T9.1 / §1947.3). A react-query list + a
 * "New method" button, per-row Edit/Delete, plus the enable/disable toggle. Every write goes through
 * the shared `api` client (CSRF handled there) and invalidates the list query on success.
 *
 * The server enforces the §1947.3 invariant — the enabled set must always keep ≥1 fee-free non-EFT
 * (`isEft=false, feeCents=0`) method — refusing any toggle/edit/delete that would violate it with a
 * 422 `non_eft_path_required`, surfaced here as a plain error so the guarantee stays visible.
 * `UNIQUE(org, method)` means creating a second row for an existing method returns 409.
 */

const PAYMENT_METHODS_KEY = ['admin', 'payment-methods'] as const;

const METHODS: PaymentMethodMethod[] = [
  'check',
  'money_order',
  'cashier_check',
  'ach',
  'card',
  'portal_eft',
  // Electronic but MANUAL (peer-to-peer, off-portal) — the landlord settles it directly. Not a
  // §1947.3 fee-free non-EFT path; that stays check/money_order/cash.
  'venmo',
];

/** Empty → undefined, so optional string fields can be coalesced to null rather than sent as "". */
function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/** Parse a user-entered dollars string to integer cents; empty/NaN → 0. */
function dollarsToCents(dollars: string): number {
  const t = dollars.trim();
  if (t === '') return 0;
  const n = parseFloat(t);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** Human-friendly method label (falls back to the enum id with underscores spaced). */
function methodName(m: PaymentMethodMethod): string {
  return m.replace(/_/g, ' ');
}

function PaymentMethodForm({ pm, onClose }: { pm: PaymentMethodDTO | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const editing = pm != null;
  const [method, setMethod] = useState<PaymentMethodMethod>(pm?.method ?? 'check');
  const [label, setLabel] = useState(pm?.label ?? '');
  const [isEft, setIsEft] = useState(pm?.isEft ?? false);
  const [enabled, setEnabled] = useState(pm?.enabled ?? true);
  const [fee, setFee] = useState(pm ? (pm.feeCents / 100).toFixed(2) : '0');
  const [instructions, setInstructions] = useState(pm?.instructions ?? '');
  const [remittanceTo, setRemittanceTo] = useState(pm?.remittanceTo ?? '');

  const save = useMutation({
    mutationFn: () => {
      const feeCents = dollarsToCents(fee);
      if (editing) {
        // `method` is fixed on edit — not sent.
        const body: PaymentMethodUpdateInput = {
          label: opt(label) ?? null,
          isEft,
          enabled,
          feeCents,
          instructions: opt(instructions) ?? null,
          remittanceTo: opt(remittanceTo) ?? null,
        };
        return api.paymentMethods.update(pm.id, body);
      }
      const body: PaymentMethodCreateInput = {
        method,
        label: opt(label) ?? null,
        isEft,
        enabled,
        feeCents,
        instructions: opt(instructions) ?? null,
        remittanceTo: opt(remittanceTo) ?? null,
        sortOrder: 0,
      };
      return api.paymentMethods.create(body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY });
      onClose();
    },
  });

  return (
    <Dialog title={editing ? 'Edit payment method' : 'New payment method'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}

        <div>
          <label htmlFor="pm-method">Method</label>
          <select
            id="pm-method"
            value={method}
            disabled={editing}
            onChange={(e) => setMethod(e.target.value as PaymentMethodMethod)}
          >
            {METHODS.map((m) => (
              <option key={m} value={m}>
                {methodName(m)}
              </option>
            ))}
          </select>
          {editing && <p className="muted">The method type cannot be changed after creation.</p>}
        </div>

        <div>
          <label htmlFor="pm-label">Label (optional)</label>
          <input
            id="pm-label"
            value={label}
            maxLength={200}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Shown to tenants (e.g. Personal check by mail)"
          />
        </div>

        <div>
          <label htmlFor="pm-fee">Added fee (USD)</label>
          <input
            id="pm-fee"
            type="number"
            min="0"
            step="0.01"
            inputMode="decimal"
            value={fee}
            onChange={(e) => setFee(e.target.value)}
            placeholder="0.00"
          />
        </div>

        <div className="row">
          <input
            id="pm-eft"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isEft}
            onChange={(e) => setIsEft(e.target.checked)}
          />
          <label htmlFor="pm-eft" style={{ margin: 0 }}>
            Electronic funds transfer (EFT)
          </label>
        </div>

        <div className="row">
          <input
            id="pm-enabled"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="pm-enabled" style={{ margin: 0 }}>
            Enabled (offered to tenants)
          </label>
        </div>

        <div>
          <label htmlFor="pm-remit">Remittance to (optional)</label>
          <input
            id="pm-remit"
            value={remittanceTo}
            maxLength={200}
            onChange={(e) => setRemittanceTo(e.target.value)}
            placeholder="Payee / address checks are made out to"
          />
        </div>

        <div>
          <label htmlFor="pm-instructions">Instructions (optional)</label>
          <textarea
            id="pm-instructions"
            value={instructions}
            rows={3}
            maxLength={2000}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="How to pay with this method (shown to tenants)"
          />
        </div>

        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create method'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function AdminPaymentMethods() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: PAYMENT_METHODS_KEY, queryFn: () => api.paymentMethods.list() });
  // `undefined` = closed; `null` = create; a method = edit.
  const [editing, setEditing] = useState<PaymentMethodDTO | null | undefined>(undefined);

  const toggle = useMutation({
    mutationFn: (m: PaymentMethodDTO) => api.paymentMethods.update(m.id, { enabled: !m.enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY }),
  });

  const remove = useMutation({
    mutationFn: (m: PaymentMethodDTO) => api.paymentMethods.remove(m.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PAYMENT_METHODS_KEY }),
  });

  if (query.isLoading) return <Loading label="Loading payment methods…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;

  const paymentMethods = query.data.paymentMethods;

  return (
    <>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <h1>Payment methods</h1>
        <button type="button" onClick={() => setEditing(null)}>
          New method
        </button>
      </div>
      <p className="subtitle">
        §1947.3 requires at least one enabled, fee-free non-electronic method at all times.
      </p>

      {toggle.isError && <StatusMessage tone="err">{errorMessage(toggle.error)}</StatusMessage>}
      {remove.isError && <StatusMessage tone="err">{errorMessage(remove.error)}</StatusMessage>}

      {paymentMethods.length === 0 ? (
        <p className="muted">No payment methods yet. Choose “New method” to add one.</p>
      ) : (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Accepted payment methods</caption>
            <thead>
              <tr>
                <th scope="col">Method</th>
                <th scope="col">Type</th>
                <th scope="col" className="num">
                  Fee
                </th>
                <th scope="col">Enabled</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paymentMethods.map((m) => (
                <tr key={m.id}>
                  <td>{m.label ?? methodName(m.method)}</td>
                  <td>{m.isEft ? 'Electronic' : 'Non-electronic'}</td>
                  <td className="num">{m.feeCents === 0 ? 'No fee' : formatCents(m.feeCents)}</td>
                  <td>
                    <span className="badge">{m.enabled ? 'Enabled' : 'Disabled'}</span>
                  </td>
                  <td>
                    <div className="row">
                      <button
                        type="button"
                        className="secondary"
                        disabled={toggle.isPending}
                        onClick={() => toggle.mutate(m)}
                      >
                        {m.enabled ? 'Disable' : 'Enable'}
                        <span className="visually-hidden"> {m.label ?? methodName(m.method)}</span>
                      </button>
                      <button type="button" className="secondary" onClick={() => setEditing(m)}>
                        Edit<span className="visually-hidden"> {m.label ?? methodName(m.method)}</span>
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete payment method ${m.label ?? methodName(m.method)}?`))
                            remove.mutate(m);
                        }}
                      >
                        Delete<span className="visually-hidden"> {m.label ?? methodName(m.method)}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing !== undefined && (
        <PaymentMethodForm pm={editing} onClose={() => setEditing(undefined)} />
      )}
    </>
  );
}
